<template>
    <modal :show.sync=show modalClasses="medium-sized-modal calculator-modal" bodyClasses="p-0" :hide-footer="true" @close="resetForm()">
        <h3 slot="header" class="mb-0 font-weight-500">Mortgage Calculator</h3>
        
        <div v-loading="loading" class="p-4">
            <div class="calculator-modal-tab mb-4" v-show="Object.keys(mortgageData).length === 0">
                <b-tabs justified class="rounded-pill p-1">
                    <b-tab title-item-class="pr-1" title="Developer Project" @click="setCurrentTab(0)" :active="currentTab === 0"></b-tab>
                    <b-tab title-item-class="pl-1" title="Subsale" @click="setCurrentTab(1)" :active="currentTab === 1"></b-tab>
                </b-tabs>
            </div>

            <ValidationObserver v-slot="{ invalid }" ref="observer">
                <b-form ref="propertyIncomeForm" role="form" @submit.prevent="calculate()">
                    <div v-show="Object.keys(mortgageData).length === 0">
                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group label="Purchase Price">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'purchasePrice'"
                                    >
                                        <CurrencyInput
                                            v-model="form.purchase_price"
                                            :options="{ currency: 'MYR', currencyDisplay: 'hidden', autoDecimalDigits: true }"
                                        />
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Loan Margin">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'loanMargin'"
                                    >
                                        <b-form-input
                                        v-model="form.margin_of_finance"
                                        type="text"
                                        maxlength="3"
                                        @keydown="preventNonNumericInput"
                                        required
                                        ></b-form-input>
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Loan Amount">
                                    <h3 class="mb-0 py-2">{{ form.loan_amount | moneyFormatter }}</h3>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Interest Rate Percent">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'interestRatePercent'"
                                    >
                                        <b-form-input
                                        v-model="form.interest_rate"
                                        type="text"
                                        maxlength="3"
                                        @keydown="preventNonNumericInput"
                                        required
                                        ></b-form-input>
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Tenure Year">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'tenureYear'"
                                    >
                                        <b-form-input
                                        v-model="form.tenure_year"
                                        type="text"
                                        maxlength="2"
                                        @keydown="preventNonNumericInput"
                                        required
                                        ></b-form-input>
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                        
                        <hr>

                        <b-row>
                            <b-col cols="12">
                                <!-- Separate content for each tab -->
                                <div>
                                    <div v-show="currentTab === 0">
                                        <!-- Content for the first tab -->
                                        <b-form-checkbox
                                            v-for="option in options"
                                            v-model="selected[option.value]"
                                            :key="option.value"
                                            :value="true"
                                        >
                                            {{ option.text }}
                                        </b-form-checkbox>
                                    </div>
                                    <div v-show="currentTab === 1">
                                        <!-- Content for the second tab -->
                                        <b-form-checkbox
                                            id="checkbox-1"
                                            v-model="form.financial_loan"
                                            name="checkbox-1"
                                            value="true"
                                            unchecked-value=""
                                        >
                                            Financial Loan Doc &amp; Valuation Costs
                                        </b-form-checkbox>
                                    </div>
                                </div>
                            </b-col>
                        </b-row>

                        <hr>

                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group label="Area">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'areaSqft'"
                                    >
                                        <b-form-input
                                        v-model="form.area_sqft"
                                        type="text"
                                        @keydown="preventNonNumericInput"
                                        required
                                        ></b-form-input>
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Monthly Rental Income">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'monthlyRentalIncome'"
                                    >
                                        <CurrencyInput
                                            v-model="form.monthly_rental_income"
                                            :options="{ currency: 'MYR', currencyDisplay: 'hidden', autoDecimalDigits: true }"
                                        />
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Monthly Utilities Expenses">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'monthlyUtilitiesExpenses'"
                                    >
                                        <CurrencyInput
                                            v-model="form.monthly_utilities_fees"
                                            :options="{ currency: 'MYR', currencyDisplay: 'hidden', autoDecimalDigits: true }"
                                        />
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </div>

                    <MortgageCalculatorTable v-if="Object.keys(mortgageData).length > 0" :mortgageData="mortgageData" />

                    <b-row>
                        <b-col md="6" sm="12" class="mx-auto text-center mt-4 pt-2">
                            <b-button type="submit" variant="primary" :disabled="invalid" v-show="Object.keys(mortgageData).length === 0">Calculate</b-button>
                            <b-button variant="primary" @click.prevent="resetForm">Reset</b-button>
                        </b-col>
                    </b-row>
                </b-form>
            </ValidationObserver>
        </div>
    </modal>
</template>
<script>
import Modal from '@/components/Modal'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import '@/assets/js/formValidations.js'
import CurrencyInput from '@/components/CurrencyInput'
import {
  notifyFunctionMixin,
  helperFunctionMixin,
  axios,
  services,
  getHeader,
} from '@/assets/js/httpImports.js'
import MortgageCalculatorTable from '@/views/Calculators/CalculatorsTables/MortgageCalculatorTable'

export default {
    props: {
        mortgage_calculator: Boolean
    },
    mixins:[ notifyFunctionMixin, helperFunctionMixin ],
    components: {
        Modal,
        ValidationObserver,
        ValidationProvider,
        CurrencyInput,
        MortgageCalculatorTable
    },
    data() {
        return {
            loading: false,
            form: {
                calculate_type: "DEVELOPER",
                purchase_price: 500000,
                margin_of_finance: 90,
                loan_amount: '',
                interest_rate: 4.2,
                tenure_year: 35,
                spa_price: 500000,
                financial_loan: '',
                area_sqft: 900,
                monthly_rental_income: '',
                monthly_utilities_fees: '',
                monthly_extra_payment: 0
            },
            currentTab: 0,
            selected: [], // Must be an array reference!
            options: [
                { text: 'Free SPA Legal Fee', value: 'free_spa' },
                { text: 'Free Loan Legal Fee', value: 'free_loan' },
                { text: 'Free Loan Stamp Duty', value: 'free_stamp' },
                { text: 'Free SPA Stamp Duty (MOT/DOA)', value: 'free_mot' },
                { text: 'No Down Payment', value: 'free_down_payment' }
            ],
            mortgageData: {},
        }
    },
    created() {
        // Calculate the initial loan_amount when the component is created
        this.calculateLoanAmount()

        // Calculate the initial rental_income when the component is created
        this.calculateRentalIncome()

        // Set all options to true by default
        this.options.forEach(option => {
            this.selected[option.value] = true
        })

        // Calculate the initial utils_expenses when the component is created
        this.calculateMonthlyUtils()
    },
    watch: {
        'form.purchase_price': {
            handler: function (newVal) {
                this.calculateLoanAmount()
                this.calculateRentalIncome()
                this.form.spa_price = newVal // Update spa_price
            },
            immediate: true, // Trigger handler immediately on component creation
        },
        'form.margin_of_finance': 'calculateLoanAmount',
        'form.area_sqft': 'calculateMonthlyUtils',
    },
    computed: {
        show: {
            get() {
                return this.mortgage_calculator;
            },
            set(newVal) {
                this.$emit('update:mortgage_calculator', newVal);
            },
        },
    },
    methods: {
        preventNonNumericInput(event) {
            const keyCode = event.keyCode || event.which;
            const keyValue = String.fromCharCode(keyCode);
            const currencyRegex = /^[\d.,$]+(\.[\d.,$]+)?$/;
            const allowedKeys = [8, 37, 38, 39, 40, 46];
            if (keyCode !== 190 && !currencyRegex.test(keyValue) && !allowedKeys.includes(keyCode)) {
                event.preventDefault();
            }
        },
        setCurrentTab(tabIndex) {
            tabIndex === 0 ? this.form.calculate_type = 'DEVELOPER': this.form.calculate_type = 'SUB_SALE'
            this.currentTab = tabIndex
        },
        calculateLoanAmount() {
            // Calculate the loan amount based on the purchase_price and margin_of_finance
            this.form.loan_amount = this.form.purchase_price * (this.form.margin_of_finance / 100) // Convert margin_of_finance to a percentag
        },
        calculateRentalIncome() {
            // Calculate the monthly_rental_income based on the purchase_price
            this.form.monthly_rental_income = (this.form.spa_price * 0.05) / 12 // Convert margin_of_finance to a percentage
        },
        calculateMonthlyUtils() {
            // Calculate the monthly_utilities_fees based on the area_sqft
            this.form.monthly_utilities_fees = this.form.area_sqft * 0.35
        },
        calculate() {
            this.loading = true
            for (const optionValue in this.selected) {
                if (this.selected[optionValue]) {
                    this.$set(this.form, optionValue, 1)
                } else {
                    this.$set(this.form, optionValue, 0)
                }
            }

            // let requestData = {
            //     calculator: this.form,
            //     client_token: 'clzNYY762WEHTP'
            // }

            // let url = `${services.fintos_calculator}mortgage`
            let url = `${services.temp_mortgage_calc}/calculator/home-loan`
			axios.post(url, this.form,{ headers: getHeader() })
			.then(res => {
				this.loading = false
                this.mortgageData = res.data
			})
			.catch(e => {
				this.errorFunction(e)
			})	  

        },
        resetForm() {
            this.form.purchase_price= 500000,
            this.form.margin_of_finance= 90,
            this.form.loan_amount= '',
            this.form.interest_rate= 4.2,
            this.form.tenure_year= 35,
            this.form.spa_price= 500000,
            this.form.financial_loan= '',
            this.form.area_sqft= 900,
            this.form.monthly_rental_income= '',
            this.form.monthly_utilities_fees= '',
            this.form.monthly_extra_payment= 0

            if (this.$options.created) {
                this.$options.created[0].call(this)
            }

            this.mortgageData = {}
        }
    }
}
</script>